import React, { useState, useEffect, useCallback } from "react";
import api from "../api/axiosConfig";
import "./SalesTracker.css";

import {
  FiBarChart2,
  FiPhoneCall,
  FiStar,
  FiArrowLeft,
  FiCheckCircle,
  FiEdit2,
  FiFileText,
  FiHash,
} from "react-icons/fi";
import { FaHandshake } from "react-icons/fa";
import { MdDashboard } from "react-icons/md";
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";

const POINT_CONFIG = {
  telephoneSalesCalls: { label: "Telephone Sales Calls", points: 1 },
  faceToFace: { label: "Face to Face", points: 1 },
  appointmentMade: { label: "Appointment Scheduled", points: 2 },
  appointmentKept: { label: "Appointment Attended", points: 2 },
  factFind: { label: "Fact Find", points: 2 },
  serviceReviews: { label: "Service Reviews", points: 1 },
  socialCalls: { label: "Social Calls", points: 1 },
  highIncomeWealthy: { label: "High Income / Wealthy", points: 2 },
  referrals: { label: "Referrals", points: 3 },
  closingInterview: { label: "Closing Interview", points: 4 },
  salesClose: { label: "Sales Close", points: 5 },
};

const mapApiToForm = (apiData) => {
  const formData = {};
  if (!apiData) return formData;
  Object.keys(POINT_CONFIG).forEach((key) => {
    formData[key] = apiData[key] || 0;
  });
  return formData;
};

const parseDateAsLocal = (dateString) => {
  if (!dateString) return new Date();
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const StatCard = ({ icon, value, title, iconBgColor }) => (
  <div className="stat-card">
    <div className="stat-icon-wrapper" style={{ backgroundColor: iconBgColor }}>
      {icon}
    </div>
    <div className="stat-info">
      <span className="stat-value">{value}</span>
      <span className="stat-title">{title}</span>
    </div>
  </div>
);

const InputField = ({ name, label, points, value, onChange, disabled }) => (
  <div className="input-group">
    <label>{label}</label>
    <input
      type="number"
      name={name}
      value={value}
      onChange={onChange}
      min="0"
      disabled={disabled}
      placeholder="0"
      onWheel={(e) => e.currentTarget.blur()} // This line fixes the scroll issue
    />
    <span className="points-text">
      {points} {points === 1 ? "point" : "points"} per entry
    </span>
  </div>
);

const SalesTracker = ({
  onBackClick,
  onEntrySubmit,
  entryDate,
  isOnLeave,
  userId = "me",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(
    Object.keys(POINT_CONFIG).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialEntry, setInitialEntry] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isEditingActive, setIsEditingActive] = useState(false);
  // MODIFIED: State now tracks totalPoints instead of totalActivities
  const [dailyStats, setDailyStats] = useState({
    totalCalls: 0,
    appointmentsKept: 0,
    faceToFaceMeetings: 0,
    serviceReviews: 0,
    salesClosed: 0,
    totalPoints: 0,
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const entryDateObj = parseDateAsLocal(entryDate);

  const daysDiff = Math.floor((today - entryDateObj) / (1000 * 60 * 60 * 24));
  const isWithinEditableWindow = daysDiff <= 2 && daysDiff >= 0;
  const isToday = today.getTime() === entryDateObj.getTime();

  const isEditable = isWithinEditableWindow && updateCount < 3 && !isOnLeave;

  // MODIFIED: This function now calculates totalPoints
  const calculateDailyStats = (entryData) => {
    const totalCalls =
      entryData.telephoneSalesCalls +
        entryData.socialCalls +
        entryData.appointmentMade || 0;
    const appointmentsKept = entryData.appointmentKept || 0;
    const faceToFaceMeetings = entryData.faceToFace || 0;
    const serviceReviews = entryData.serviceReviews || 0;
    const salesClosed = entryData.salesClose || 0;

    const totalPoints = Object.keys(POINT_CONFIG).reduce(
      (sum, key) =>
        sum + (Number(entryData[key]) || 0) * (POINT_CONFIG[key]?.points || 0),
      0
    );

    return {
      totalCalls,
      appointmentsKept,
      faceToFaceMeetings,
      serviceReviews,
      salesClosed,
      totalPoints,
    };
  };

  const fetchEntryData = useCallback(() => {
    setIsLoading(true);
    const blankForm = Object.keys(POINT_CONFIG).reduce(
      (acc, key) => ({ ...acc, [key]: "" }),
      {}
    );

    api
      .get(`/api/users/${userId}/entry-by-date?date=${entryDate}`)
      .then((res) => {
        const currentEntry = res.data;

        if (currentEntry && currentEntry.id) {
          const loadedFormData = mapApiToForm(currentEntry);
          setFormData(loadedFormData);
          setInitialEntry(loadedFormData);
          setUpdateCount(currentEntry.updateCount || 0);
          setIsEditMode(true);
          setIsEditingActive(false);
          setDailyStats(calculateDailyStats(currentEntry));
        } else {
          setFormData(blankForm);
          setInitialEntry(blankForm);
          setUpdateCount(0);
          setIsEditMode(false);
          setIsEditingActive(isWithinEditableWindow && !isOnLeave);
          setDailyStats(calculateDailyStats({}));
        }
      })
      .catch((err) => {
        setFormData(blankForm);
        setInitialEntry(blankForm);
        setUpdateCount(0);
        setIsEditMode(false);
        setIsEditingActive(isWithinEditableWindow && !isOnLeave);
        setDailyStats(calculateDailyStats({}));
        console.error("Failed to fetch entry data:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [entryDate, isWithinEditableWindow, userId, isOnLeave]);

  useEffect(() => {
    fetchEntryData();
  }, [fetchEntryData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? "" : Math.max(0, parseInt(value, 10) || 0),
    }));
  };

  const handleCancelEdit = () => {
    setFormData(initialEntry);
    setIsEditingActive(false);
    setSnackbar({ open: true, message: "Edit canceled.", severity: "info" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isEditable || !isEditingActive) return;

    const hasData = Object.values(formData).some((val) => Number(val) > 0);
    if (!hasData) {
      setSnackbar({
        open: true,
        message: "Please enter at least one activity.",
        severity: "info",
      });
      return;
    }

    if (isEditMode) {
      const hasChanged = Object.keys(formData).some(
        (key) => String(formData[key] || 0) !== String(initialEntry[key] || 0)
      );
      if (!hasChanged) {
        setSnackbar({
          open: true,
          message: "No changes have been made.",
          severity: "info",
        });
        return;
      }
    }

    setIsSubmitting(true);
    const totalPoints = Object.keys(formData).reduce(
      (sum, key) =>
        sum + (Number(formData[key]) || 0) * (POINT_CONFIG[key]?.points || 0),
      0
    );

    const submissionData = {
      entryDate: entryDate,
      points: totalPoints,
      ...formData,
    };

    try {
      const response = await api.post(
        `/api/users/${userId}/entry`,
        submissionData
      );
      const savedEntry = response.data.entry;

      const newUpdateCount = savedEntry.updateCount || 0;
      const loadedFormData = mapApiToForm(savedEntry);

      setFormData(loadedFormData);
      setInitialEntry(loadedFormData);
      setUpdateCount(newUpdateCount);
      setIsEditMode(true);
      setIsEditingActive(false);
      setDailyStats(calculateDailyStats(savedEntry));

      if (newUpdateCount >= 3) {
        setSnackbar({
          open: true,
          message: "Entry saved. No more edits are allowed for this date.",
          severity: "warning",
        });
      } else {
        setSnackbar({
          open: true,
          message: `Entry saved! ${3 - newUpdateCount} edit(s) remaining.`,
          severity: "success",
        });
      }
      if (onEntrySubmit) {
        await onEntrySubmit();
      }
    } catch (err) {
      const message = err.response?.data?.message || "Failed to save entry.";
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const viewingDate = entryDateObj;

  const getFormTitle = () => {
    const dateStr = `${viewingDate.getDate()} ${viewingDate.toLocaleDateString(
      "en-GB",
      { month: "short" }
    )}, ${viewingDate.getFullYear()}`;
    if (isOnLeave) return "On Leave - View Only";
    if (isEditingActive)
      return isToday
        ? "Submiting Today's Entry"
        : `Editing Entry for ${dateStr}`;
    if (!isEditMode && isEditable) return "Daily Sales Input";
    return `Viewing Entry for ${dateStr}`;
  };

  return (
    <div className="sales-tracker-container">
      <header className="tracker-header">
        <div className="header-left">
          <div className="logo-icon">
            <FiBarChart2 />
          </div>
          <div className="header-text">
            <h1>Sales Performance Tracker</h1>
            <p className="month-display">
              Daily summary for{" "}
              {`${viewingDate.getDate()} ${viewingDate.toLocaleDateString(
                "en-GB",
                { month: "short" }
              )}, ${viewingDate.getFullYear()}`}
            </p>
          </div>
        </div>
        <div className="header-right">
          <button className="back-to-dashboard-btn" onClick={onBackClick}>
            <MdDashboard /> Dashboard
          </button>
        </div>
      </header>

      <section className="stats-grid top-row">
        <StatCard
          icon={<FiPhoneCall />}
          value={dailyStats.totalCalls}
          title="Total Calls"
          iconBgColor="#e7f0ff"
        />
        <StatCard
          icon={<FiStar />}
          value={dailyStats.appointmentsKept}
          title="Appointments Attended"
          iconBgColor="#fdeee7"
        />
        <StatCard
          icon={<FiCheckCircle />}
          value={dailyStats.faceToFaceMeetings}
          title="Face-to-Face Meetings"
          iconBgColor="#e0f7e0"
        />
      </section>
      <section className="stats-grid bottom-row">
        <StatCard
          icon={<FiFileText />}
          value={dailyStats.serviceReviews}
          title="Service Reviews"
          iconBgColor="#e0f7fa"
        />
        <StatCard
          icon={<FaHandshake />}
          value={dailyStats.salesClosed}
          title="Sales Closed"
          iconBgColor="#e4f0ff"
        />
        {/* MODIFIED: This card now displays total points */}
        <StatCard
          icon={<FiHash />}
          value={dailyStats.totalPoints}
          title="Total Points"
          iconBgColor="#fff3cd"
        />
      </section>

      <section className="daily-input-card">
        <form onSubmit={handleSubmit}>
          <div className="form-header">
            <FiArrowLeft className="back-arrow-icon" onClick={onBackClick} />
            <div className="form-title-wrapper">
              <h2>{getFormTitle()}</h2>
              {isEditMode && isEditable && !isEditingActive && (
                <button
                  type="button"
                  className="edit-header-btn"
                  onClick={() => setIsEditingActive(true)}
                >
                  <FiEdit2 /> Edit
                </button>
              )}
            </div>
          </div>

          <div className="form-grid">
            <div className="grid-top">
              {Object.keys(POINT_CONFIG)
                .slice(0, 9)
                .map((key) => (
                  <InputField
                    key={key}
                    name={key}
                    label={POINT_CONFIG[key].label}
                    points={POINT_CONFIG[key].points}
                    value={formData[key]}
                    onChange={handleChange}
                    disabled={!isEditingActive}
                  />
                ))}
            </div>
            <div className="grid-bottom">
              {Object.keys(POINT_CONFIG)
                .slice(9)
                .map((key) => (
                  <InputField
                    key={key}
                    name={key}
                    label={POINT_CONFIG[key].label}
                    points={POINT_CONFIG[key].points}
                    value={formData[key]}
                    onChange={handleChange}
                    disabled={!isEditingActive}
                  />
                ))}
            </div>
          </div>

          <div className="form-footer">
            {isEditable && isEditingActive ? (
              <div className="form-footer-buttons">
                {isEditMode && (
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : isEditMode
                    ? "Update Report"
                    : "Submit Report"}
                </button>
              </div>
            ) : (
              <Typography color="text.secondary" align="center" variant="body2">
                {isOnLeave
                  ? "You are on leave and cannot make changes."
                  : !isWithinEditableWindow
                  ? "This entry is outside the editable window and is locked."
                  : updateCount >= 3
                  ? "No more changes can be made for this entry."
                  : "This entry is read-only. Click Edit to make changes."}
              </Typography>
            )}
          </div>
        </form>
      </section>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={handleSnackbarClose}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SalesTracker;
